import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useQueryParams from 'hooks/useQueryParams'
import { searchVerticalItemsByKey } from 'constants/search'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import CruisesTabContent from '../Components/Mobile/TabContents/CruisesTabContent'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { getCruiseGlobalFilters, getCruiseSearchStateFromParams } from 'lib/cruises/cruiseUtils'
import * as Analytics from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import qs from 'qs'
import { pushWithRegion } from 'actions/NavigationActions'

function useGlobalSearchVerticalTabCruises(): App.GlobalSearchVerticalTabV2 {
  const dispatch = useAppDispatch()

  const queryParams = useQueryParams()
  const initialState = useMemo(() => getCruiseSearchStateFromParams(queryParams), [queryParams])
  const {
    globalCruisesSearchDispatch: searchDispatch,
    globalCruisesSearchState: state,
  } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialState,
  })

  const onSearch = useCallback(() => {
    const {
      searchItems: globalSearchItems,
      secondarySearchItems: globalSecondarySearchItems,
      flexibleMonths: globalFlexibleMonths,
      durationMin: globalDurationMin,
      durationMax: globalDurationMax,
      durationRange: globalDurationRange,
      checkinDate: globalCheckinDate,
      checkoutDate: globalCheckoutDate,
      cruiseLines: globalCruiseLines,
    } = state

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItems: globalSearchItems,
        secondarySearchItems: globalSecondarySearchItems,
        searchVerticals: new Set(['cruises']),
        flexibleMonths: globalFlexibleMonths,
        durationMin: globalDurationMin,
        durationMax: globalDurationMax,
        durationRange: globalDurationRange,
        checkinDate: globalCheckinDate,
        checkoutDate: globalCheckoutDate,
        cruiseLines: globalCruiseLines,
      }),
    }))
    const cruiseGlobalFilters = getCruiseGlobalFilters(state)
    const query = qs.stringify(cruiseGlobalFilters, { arrayFormat: 'repeat' })
    dispatch(pushWithRegion('/search/cruises', query))
  }, [dispatch, state])

  const intl = useIntl()

  return useMemo((): App.GlobalSearchVerticalTabV2 => ({
    label: searchVerticalItemsByKey.get('cruises')?.label ?? intl.formatMessage({
      id: 'Ts3crhs4GnWIqYRvdeSu',
      defaultMessage: 'cruises',
      description: 'Fallback label for cruises search vertical tab in the cruises section',
    }),
    key: 'cruises',
    context: { dispatch: searchDispatch, state },
    onSearch,
    ContentComponent: CruisesTabContent,
    Icon: LineShipIcon,
  }), [searchDispatch, state, onSearch, intl])
}

export default useGlobalSearchVerticalTabCruises
